import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import { useShopper } from '../../../helpers/ShopperContext';
import VerificationRecordList from './VerificationRecordList';
import VerificationStatusBanner from './VerificationStatusBanner';
import VerifiedSellerForm from './VerifiedSellerForm';
import './VerificationCenter.css';

const API_PATHS = {
  seller: '/v1/seller/verification',
  shopper: '/v1/shopper/verification',
};

export function getVerificationCenterState(data) {
  if (data?.verification?.isVerified) return 'verified';
  if (data?.verification?.revokedAt) return 'revoked';

  const sellerRecord = data?.ageVerifications?.find(record => (
    record.complianceType === 'verified-seller'
  ));
  if (data?.blockedReason === 'pending_review' || sellerRecord?.status === 'submitted') {
    return 'pending';
  }
  if (sellerRecord?.status === 'rejected') return 'rejected';
  return 'not-started';
}

async function readResponse(response) {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body.error || body.message || 'Verification request failed.');
  }
  return body.data;
}

export default function VerificationCenter({
  variant = 'page',
  scope = 'seller',
  onComplete,
  formFields,
  formCopy,
}) {
  const auth = useAuth();
  const shopper = useShopper();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const apiPath = API_PATHS[scope] || API_PATHS.seller;
  const request = scope === 'shopper' ? shopper.fetch : auth.fetch;
  const apiBase = scope === 'shopper' ? shopper.apiBase : (auth.API_URL || '');
  const apiUrl = `${apiBase}${apiPath}`;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request(apiUrl, {
        headers: { Accept: 'application/json' },
      });
      const nextData = await readResponse(response);
      setData(nextData);
      return nextData;
    } catch (requestError) {
      setError(requestError.message || 'Verification status could not be loaded.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiUrl, request]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const state = getVerificationCenterState(data);
  const sellerRecord = data?.ageVerifications?.find(record => (
    record.complianceType === 'verified-seller'
  ));
  const initialValues = useMemo(() => ({
    legalName: sellerRecord?.legalName || '',
    dba: sellerRecord?.dba || '',
  }), [sellerRecord?.legalName, sellerRecord?.dba]);
  const showForm = state !== 'verified';
  const formDisabled = !data?.canSubmit || state === 'pending';

  const submitVerification = async formData => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await request(apiUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const submitted = await readResponse(response);
      await loadStatus();
      onComplete?.(submitted);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className={`verification-center verification-center--${variant}`} aria-busy="true">
        <p>Loading verification status…</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={`verification-center verification-center--${variant}`}>
        <div className="verification-inline-error" role="alert">{error}</div>
        <button type="button" onClick={loadStatus}>Try again</button>
      </div>
    );
  }

  return (
    <div className={`verification-center verification-center--${variant}`}>
      <VerificationStatusBanner
        state={state}
        verification={data?.verification}
        submission={sellerRecord}
      />

      {error && <div className="verification-inline-error" role="alert">{error}</div>}

      {state !== 'verified' && data?.payoutProfile?.isEligibleCountry && (
        <aside className="verification-payout-alternative">
          <strong>You may already have another path to verification.</strong>
          <p>
            An approved payout profile also verifies your storefront, without a separate
            identity submission here.
          </p>
          <a href="/account/payments/profile">Complete your payout profile</a>
        </aside>
      )}

      <VerificationRecordList
        payoutProfile={data?.payoutProfile}
        records={data?.ageVerifications}
      />

      {showForm && (
        <VerifiedSellerForm
          fields={formFields}
          copy={formCopy}
          initialValues={initialValues}
          disabled={formDisabled}
          submitting={submitting}
          onSubmit={submitVerification}
        />
      )}
    </div>
  );
}
